import React, { useCallback, useMemo, useState } from 'react';

import { TbDots } from 'react-icons/tb';

import dayjs from 'dayjs';

import useCancelJobs from 'state/remote/remote-organization/use-cancel-jobs';

import Button from 'components-new/button/button';

import type { DerivedJobAd } from 'state/__generated__/tadaahAPI';

import DropdownMenu from 'components-new/dropdown-menu/dropdown-menu';

import type { DropdownMenuItemType } from 'components-new/dropdown-menu/dropdown-menu-item';
import CancelJobDialog from 'pages/organization/OrganizationWeekPlannerPage/components/job-popover/cancel-job-dialog';

import client from 'state/remote/client';
import { getApiOrganisationPeriodJobsAssignmentDetailsListQueryKey } from 'state/__generated__/tadaahAPI';

import { showSuccessToast } from 'components/toast/toast';
import { createLocale } from 'locales';

import ConvertToUrgentDialog from './ConvertToUrgentDialog';

// import useCancelJobAd from 'state/remote/remote-organization/use-cancel-job-ad';
const DAYJS_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const localize = createLocale('global');
const periodJobsLocalize = createLocale('organisationPeriodJobsLocales');

interface AssignmentOptionsTypes {
  assignment: DerivedJobAd;
  periodJobId: string;
}

const AssignmentOptions: React.FC<AssignmentOptionsTypes> = ({
  assignment,
  periodJobId,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [isConvertDialogOpen, setIsConvertDialogOpen] =
    useState<boolean>(false);
  const cancellCallBack = useCallback(() => {
    showSuccessToast({
      description: periodJobsLocalize('jobCancelled'),
      title: localize('done'),
    });

    client.invalidateQueries(
      getApiOrganisationPeriodJobsAssignmentDetailsListQueryKey(
        periodJobId.toString() || '',
      )?.[0],
    );
  }, [periodJobId]);

  const cancelJobMutation = useCancelJobs(cancellCallBack);

  const onCancelDialogAcceptClicked = (reason?: string) => {
    cancelJobMutation.mutate({ jobs: [assignment.id || 0], reason });
    setIsCancelDialogOpen(false);
  };

  const startDate = useMemo(() => {
    return dayjs(`${assignment.date} ${assignment.start}`, DAYJS_FORMAT);
  }, [assignment]);

  const dropdownMenuItems: DropdownMenuItemType[] = [];
  if (!assignment.accepted)
    dropdownMenuItems.push({
      key: 'edit',
      label: periodJobsLocalize('edit'),
      onClick: () =>
        window.location.assign(
          `/organisation/job/${assignment.id}/edit?next=/organisation/period_jobs/?assignment_details=true%26job_id=${periodJobId}`,
        ),
    });

  if (!assignment.accepted && !assignment.converted) {
    dropdownMenuItems.push({
      key: 'convert',
      label: periodJobsLocalize('convertToUrgent'),
      onClick: () => setIsConvertDialogOpen(true),
    });
    dropdownMenuItems.push({
      key: 'invite',
      label: periodJobsLocalize('inviteFreelancer'),
      onClick: () =>
        window.location.assign(
          `/organisation/period_jobs/${periodJobId}/${assignment.id}/`,
        ),
    });
  }

  dropdownMenuItems.push({
    key: 'cancel',
    label: periodJobsLocalize('cancelAssignment'),
    onClick: () => setIsCancelDialogOpen(true),
    variant: 'destructive',
  });

  const cancelJobDialogVariant = useMemo(():
    | undefined
    | 'explain'
    | 'compensate' => {
    const startDatetimeBeforeOneDay = startDate.add(-1, 'day');
    const startDatetimeAfterOneHour = startDate.add(1, 'h');

    if (assignment.assigned_to) {
      if (
        startDatetimeBeforeOneDay.isBefore(dayjs()) &&
        startDatetimeAfterOneHour.isAfter(dayjs())
      ) {
        return 'compensate';
      }

      if (startDatetimeBeforeOneDay.isAfter(dayjs())) {
        return 'explain';
      }
    }

    return undefined;
  }, [assignment.assigned_to, startDate]);

  return (
    <>
      <div className="w-10">
        <DropdownMenu
          autoplacement
          menuButton={() => (
            <Button
              label=""
              leftIconProps={{ icon: TbDots }}
              variant="transparent"
              className="hover:bg-transparent"
            />
          )}
          items={dropdownMenuItems}
          menuItemsClassName="z-50"
          position="left-start"
        />
      </div>
      <CancelJobDialog
        isOpen={isCancelDialogOpen}
        onAcceptClicked={onCancelDialogAcceptClicked}
        onBackClicked={() => setIsCancelDialogOpen(false)}
        onCloseClicked={() => setIsCancelDialogOpen(false)}
        variant={cancelJobDialogVariant}
        job={{
          competence: assignment.competence || '',
          date: assignment.date,
          end: assignment.end,
          freelancer: assignment.assigned_to?.name,
          location: assignment.location.name || '',
          start: assignment.start,
        }}
      />
      <ConvertToUrgentDialog
        periodJobId={periodJobId}
        assignment={assignment}
        isOpen={isConvertDialogOpen}
        onCloseClicked={() => setIsConvertDialogOpen(false)}
      />
    </>
  );
};

export default AssignmentOptions;
